import React from "react";
import {
  ResponsiveContainer,
  ComposedChart,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Area,
  Bar,
  Legend,
} from "recharts";

function formatDay(iso) {
  // YYYY-MM-DD -> dd/mm
  if (!iso) return "";
  const [, m, d] = iso.split("-");
  return `${d}/${m}`;
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const sub = payload.find((p) => p.dataKey === "subscribers")?.value ?? 0;
  const rev = payload.find((p) => p.dataKey === "revenue")?.value ?? 0;
  const dl = payload.find((p) => p.dataKey === "downloads")?.value ?? 0;
  return (
    <div className="bg-[#FFFFFF] border border-[#0B192C] px-4 py-3 shadow-lg text-xs">
      <p className="font-mono text-[#0B192C] mb-2">{label}</p>
      <p className="text-[#0E7C8F]">
        <span className="inline-block w-2 h-2 bg-[#0E7C8F] mr-2" />
        Iscritti: <strong>{sub}</strong>
      </p>
      <p className="text-[#E89B6C]">
        <span className="inline-block w-2 h-2 bg-[#E89B6C] mr-2" />
        Ricavi: <strong>€ {Number(rev).toFixed(2).replace(".", ",")}</strong>
      </p>
      <p className="text-[#3A4F6B]">
        <span className="inline-block w-2 h-2 bg-[#3A4F6B] mr-2" />
        Download PDF: <strong>{dl}</strong>
      </p>
    </div>
  );
}

export default function AdminCharts({ data, days, onDaysChange }) {
  const series = data?.series || [];

  return (
    <section
      data-testid="admin-charts"
      className="border-b border-[#E5E1DA] bg-[#FFFFFF]"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-8">
        <div className="flex items-center justify-between gap-4 mb-6">
          <div>
            <p className="overline">Andamento</p>
            <h2 className="font-serif-display text-2xl md:text-3xl mt-1">
              Ultimi <em className="text-[#0E7C8F]">{days}</em> giorni
            </h2>
          </div>
          <div className="flex gap-1 border border-[#0B192C]/15">
            {[7, 30, 90].map((d) => (
              <button
                key={d}
                onClick={() => onDaysChange(d)}
                data-testid={`admin-chart-range-${d}`}
                className={`px-3 py-2 text-[10px] uppercase tracking-[0.18em] transition-colors ${
                  d === days
                    ? "bg-[#0B192C] text-[#F9F8F6]"
                    : "text-[#0B192C] hover:bg-[#F9F8F6]"
                }`}
              >
                {d}g
              </button>
            ))}
          </div>
        </div>

        <div className="h-72 md:h-80">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={series}
              margin={{ top: 8, right: 16, left: 0, bottom: 0 }}
            >
              <defs>
                <linearGradient id="subGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#0E7C8F" stopOpacity={0.55} />
                  <stop offset="100%" stopColor="#0E7C8F" stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#E5E1DA" strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                tickFormatter={formatDay}
                tick={{ fontSize: 11, fill: "#3A4F6B" }}
                stroke="#E5E1DA"
              />
              <YAxis
                yAxisId="left"
                tick={{ fontSize: 11, fill: "#3A4F6B" }}
                stroke="#E5E1DA"
                allowDecimals={false}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                tickFormatter={(v) => `€${v}`}
                tick={{ fontSize: 11, fill: "#3A4F6B" }}
                stroke="#E5E1DA"
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                wrapperStyle={{ fontSize: 11, color: "#3A4F6B" }}
                iconType="square"
              />
              <Area
                yAxisId="left"
                type="monotone"
                dataKey="subscribers"
                name="Iscritti"
                stroke="#0E7C8F"
                fill="url(#subGrad)"
                strokeWidth={2}
              />
              <Bar
                yAxisId="right"
                dataKey="revenue"
                name="Ricavi (€)"
                fill="#E89B6C"
                radius={[2, 2, 0, 0]}
              />
              <Bar
                yAxisId="left"
                dataKey="downloads"
                name="Download PDF"
                fill="#3A4F6B"
                opacity={0.35}
                radius={[2, 2, 0, 0]}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
    </section>
  );
}
